import { useUser } from "@clerk/clerk-react";
import {
    CameraIcon,
    CheckCircleIcon,
    PencilSquareIcon,
    ShareIcon,
    XCircleIcon
} from "@heroicons/react/24/outline";
import {
    ChevronDownIcon, EnvelopeIcon
} from "@heroicons/react/24/solid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    FaConciergeBell,
    FaList,
    FaMap,
    FaMapMarkerAlt,
    FaStar
} from "react-icons/fa";
import { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useNavigate, useParams } from "react-router-dom";
import MapView from "../../components/MapView/MapView";
import RecommendationCard from "../../components/RecommendationCard/RecommendationCard";
import ReviewModal from "../../components/ReviewModal/ReviewModal";
import ShareProfileModal from "../../components/ShareProfileModal/ShareProfileModal";
import TrustScoreWheel from "../../components/TrustScoreWheel/TrustScoreWheel";
import { API_URL } from "../../utils/constants";
import Profile from "../Profile/Profile";
import "../Profile/Profile.css";
import "./PublicProfile.css";

const StarRatingDisplay = ({ rating }) => {
    const numRating = parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalf = numRating - fullStars >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

    return (
        <div className="star-rating">
            {[...Array(fullStars)].map((_, i) => <FaStar key={`full-${i}`} className="filled" />)}
            {hasHalf && <FaStar key={`half-${Date.now()}`} className="half" />}
            {[...Array(emptyStars)].map((_, i) => <FaStar key={`empty-${i}`} className="empty" />)}
        </div>
    );
};

// Component removed - now using shared RecommendationCard component

// Add this badge component after the existing components but before PublicProfile component
const AchievementBadge = ({ recCount }) => {
    const getBadgeInfo = (count) => {
        if (count >= 100) {
            return {
                level: 'Diamond',
                tier: 'Diamond Recommender',
                className: 'achievement-badge-diamond',
                icon: '💎',
                description: `Diamond Recommender (${count} recommendations)`
            };
        } else if (count >= 50) {
            return {
                level: 'Platinum',
                tier: 'Platinum Recommender',
                className: 'achievement-badge-platinum',
                icon: '⭐',
                description: `Platinum Recommender (${count} recommendations)`
            };
        } else if (count >= 25) {
            return {
                level: 'Gold',
                tier: 'Gold Recommender',
                className: 'achievement-badge-gold',
                icon: '🏆',
                description: `Gold Recommender (${count} recommendations)`
            };
        } else if (count >= 10) {
            return {
                level: 'Silver',
                tier: 'Silver Recommender',
                className: 'achievement-badge-silver',
                icon: '🥈',
                description: `Silver Recommender (${count} recommendations)`
            };
        } else if (count >= 1) {
            return {
                level: 'Bronze',
                tier: 'Bronze Recommender',
                className: 'achievement-badge-bronze',
                icon: '🥉',
                description: `Bronze Recommender (${count} recommendations)`
            };
        }
        return null;
    };

    const badge = getBadgeInfo(recCount);

    if (!badge) {
        // Show placeholder for users with 0 recommendations (in public view)
        return (
            <div className="achievement-badge-starter" title="This user hasn't shared any recommendations yet">
                <div className="achievement-badge-icon">🌟</div>
            </div>
        );
    }

    return (
        <div className={`achievement-badge-circular ${badge.className}`} title={badge.description}>
            <div className="achievement-badge-icon">{badge.icon}</div>
        </div>
    );
};

const UnfollowConfirmationModal = ({ isOpen, onClose, onConfirm, userName, isLoading }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h2 style={{ marginTop: 0 }}>Unfollow {userName}?</h2>
                <p>
                    Their recommendations will no longer appear in your feed. You can always follow them back later.
                </p>
                <div className="modal-buttons" style={{ justifyContent: 'flex-end' }}>
                    <button type="button" onClick={onClose} className="cancel-button" disabled={isLoading}>
                        Cancel
                    </button>
                    <button 
                        type="button" 
                        onClick={onConfirm} 
                        className="submit-button danger" 
                        disabled={isLoading}
                    >
                        {isLoading ? 'Processing...' : 'Unfollow'}
                    </button>
                </div>
            </div>
        </div>
    );
};

function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
    );
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error("Canvas is empty"));
                    return;
                }
                blob.name = fileName;
                resolve(blob);
            },
            "image/jpeg",
            0.95
        );
    });
}

const PublicProfile = () => {
    const { username } = useParams();
    const { user, isLoaded, isSignedIn } = useUser();
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUserEmail, setCurrentUserEmail] = useState(null);
    const [profileInfo, setProfileInfo] = useState(null);
    const [connections, setConnections] = useState([]);
    const [recommendations, setRecommendations] = useState([]);
    const [likedMap, setLikedMap] = useState(new Map());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [selectedProviderForReview, setSelectedProviderForReview] = useState(null);
    const [imageFailed, setImageFailed] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('not_connected');
    const [isFollowLoading, setIsFollowLoading] = useState(false);
    const [isUnfollowModalOpen, setIsUnfollowModalOpen] = useState(false);
    // const [searchQuery, setSearchQuery] = useState("");
    // const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
    const [selectedCities, setSelectedCities] = useState([]);
    const [showCityFilter, setShowCityFilter] = useState(false);
    const [selectedServices, setSelectedServices] = useState([]);
    const [showServiceFilter, setShowServiceFilter] = useState(false);
    const [userScore, setUserScore] = useState(0);
    // Batch comments state
    const [commentsMap, setCommentsMap] = useState(new Map());
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    
    // Add state for total recommendation count
    const [totalRecommendationCount, setTotalRecommendationCount] = useState(0);
    const [unfilteredRecommendationCount, setUnfilteredRecommendationCount] = useState(0);
    
    // Filter options from server (like TrustCircles)
    const [availableServices, setAvailableServices] = useState([]);
    const [availableCities, setAvailableCities] = useState([]);
    const [serviceIdCounts, setServiceIdCounts] = useState({});
    
    // Check if this is the current user's own profile
    const [isOwnProfile, setIsOwnProfile] = useState(false);
    const [currentUserUsername, setCurrentUserUsername] = useState(null);
    const [isProfileOwnershipChecked, setIsProfileOwnershipChecked] = useState(false);

    // Profile editing state
    const [isEditing, setIsEditing] = useState(false);
    const [editingBio, setEditingBio] = useState("");
    const [saving, setSaving] = useState(false);
    const [imgSrcForCropper, setImgSrcForCropper] = useState("");
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [originalFile, setOriginalFile] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedRecForEdit, setSelectedRecForEdit] = useState(null);
    
    // Refs for cropping
    const imgRef = useRef(null);
    
    // Constants for cropping
    const ASPECT_RATIO = 1;
    const MIN_DIMENSION = 150;

    // List/Map view state
    const [viewMode, setViewMode] = useState("list"); // 'list' or 'map'
    const [userLocation, setUserLocation] = useState(null);

    const navigate = useNavigate();

    useEffect(() => {
        if (isLoaded && user) {
            setCurrentUserId(user.id);
            setCurrentUserEmail(user.primaryEmailAddress?.emailAddress);
        } else if (isLoaded && !isSignedIn) {
            setCurrentUserId(null);
            setCurrentUserEmail(null);
        }
    }, [isLoaded, isSignedIn, user]);

    // Add this useEffect to get user location for map view
    useEffect(() => {
        if (viewMode === "map" && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    console.log("Error getting location:", error);
                    setUserLocation(null);
                }
            );
        }
    }, [viewMode]);

    // Determine if this is the user's own profile
    useEffect(() => {
        // Reset check state when starting
        setIsProfileOwnershipChecked(false);
        
        if (!isSignedIn || !user || !username) {
            setIsOwnProfile(false);
            setIsProfileOwnershipChecked(true);
            return;
        }

        const checkProfileOwnership = async () => {
            try {
                // Get current user's basic info including username
                const params = new URLSearchParams({
                    user_id: user.id,
                    email: user.primaryEmailAddress?.emailAddress || '',
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    phoneNumber: user.primaryPhoneNumber?.phoneNumber || ''
                });
                
                const response = await fetch(`${API_URL}/api/users/me/basic-info?${params.toString()}`);
                if (response.ok) {
                    const data = await response.json();
                    const userUsername = data.userUsername;
                    setCurrentUserUsername(userUsername);
                    setIsOwnProfile(userUsername === username);
                    // console.log('Profile ownership check:', {
                    //     username,
                    //     userUsername: data?.userUsername,
                    //     isOwnProfile: data?.userUsername === username,
                    //     hasClerkUsername: !!user.username,
                    //     hasEmail: !!user.primaryEmailAddress?.emailAddress
                    // });
                } else {
                    console.error('Failed to fetch user basic info:', response.status);
                    setIsOwnProfile(false);
                }
                setIsProfileOwnershipChecked(true);
            } catch (error) {
                console.error('Error checking profile ownership:', error);
                // Fallback to false if there's an error
                setIsOwnProfile(false);
                setIsProfileOwnershipChecked(true);
            }
        };

        checkProfileOwnership();
    }, [isSignedIn, user, username]);

    const fetchFilterOptions = useCallback(async () => {
        if (!username || !isLoaded || !isProfileOwnershipChecked) return;
        
        try {
            const params = new URLSearchParams();
            if (user) {
                params.set('loggedInUserId', user.id);
            }
            
            // Add current filter state to get filtered options
            if (selectedServices.length > 0) {
                params.set('services', selectedServices.join(','));
            }
            if (selectedCities.length > 0) {
                params.set('cities', selectedCities.join(','));
            }
            
            const response = await fetch(
                `${API_URL}/api/users/pro/${username}/filter-options?${params.toString()}`
            );
            if (!response.ok) {
                throw new Error('Failed to fetch filter options');
            }
            
            const data = await response.json();
            if (data.success) {
                setAvailableServices(data.services || []);
                setAvailableCities(data.cities || []);
                setServiceIdCounts(data.serviceIdCounts || {});
            }
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    }, [username, isLoaded, isProfileOwnershipChecked, user, selectedServices, selectedCities]);

    // Fetch filter options when component loads or filters change
    useEffect(() => {
        if (isProfileOwnershipChecked) {
            fetchFilterOptions();
        }
    }, [fetchFilterOptions, isProfileOwnershipChecked]);

    const handleServiceSelection = (serviceId) => {
        setSelectedServices((prev) => {
            const newServices = prev.includes(serviceId) 
                ? prev.filter((s) => s !== serviceId) 
                : [...prev, serviceId];
            
            // Reset to page 1 when filters change
            setCurrentPage(1);
            return newServices;
        });
        // Close the service filter dropdown when a selection is made
        setShowServiceFilter(false);
    };

    const handleCitySelection = (cityName) => {
        setSelectedCities((prev) => {
            const newCities = prev.includes(cityName) 
                ? prev.filter((c) => c !== cityName) 
                : [...prev, cityName];
            
            // Reset to page 1 when filters change
            setCurrentPage(1);
            return newCities;
        });
        // Close the city filter dropdown when a selection is made
        setShowCityFilter(false);
    };

    // Debounce search query to prevent API calls on every keystroke
    // useEffect(() => {
    //     // Only trigger search on Enter key press, not automatically
    //     // const timer = setTimeout(() => {
    //     //     setDebouncedSearchQuery(searchQuery);
    //     // }, 500); // Wait 500ms after user stops typing

    //     // return () => clearTimeout(timer);
    // }, [searchQuery]);

    const fetchPageData = useCallback(async (page = 1) => {
        if (!username || !isLoaded || !isProfileOwnershipChecked) return;
        
        if (page === 1) {
            setLoading(true);
            // Also fetch filter options on first page load
            fetchFilterOptions();
        } else {
            setIsLoadingPage(true);
        }
        
        setError(null);
        const loggedInUserId = user ? user.id : null;  
        // console.log('PublicProfile debug:', {
        //     username,
        //     isOwnProfile,
        //     user: user ? { id: user.id, email: user.primaryEmailAddress?.emailAddress } : null,
        //     loggedInUserId
        // });
        
        try {
            // Build query parameters
            const params = new URLSearchParams();
            
            if (isOwnProfile) {
                params.set('user_id', user.id);
                params.set('email', user.primaryEmailAddress?.emailAddress);
                params.set('limit', '20');
                params.set('offset', ((page - 1) * 20).toString());
            } else {
                params.set('loggedInUserId', loggedInUserId || '');
                params.set('limit', '25');
                params.set('offset', ((page - 1) * 25).toString());
            }
            
            // Add filter parameters for both own profile and public profile
            // if (debouncedSearchQuery.trim()) {
            //     params.set('search', debouncedSearchQuery.trim());
            //     // console.log('Adding search parameter:', debouncedSearchQuery.trim());
            // }
            if (selectedServices.length > 0) {
                params.set('services', selectedServices.join(','));
            }
            if (selectedCities.length > 0) {
                params.set('cities', selectedCities.join(','));
            }
            
            // Use the appropriate endpoint based on whether it's own profile or not
            const endpoint = isOwnProfile 
                ? `${API_URL}/api/users/me/recommendations?${params.toString()}`
                : `${API_URL}/api/users/pro/${username}?${params.toString()}`;
                
            // console.log('Calling endpoint:', endpoint);
            // console.log('Parameters:', params.toString());
            const profileRes = await fetch(endpoint);
            if (!profileRes.ok) throw new Error("Failed to fetch profile data");
            const data = await profileRes.json();
            
            let processedRecommendations = [];
            
            if (isOwnProfile) {
                // Transform the data structure to match what the component expects
                setProfileInfo({
                    userId: data.userId,
                    userName: data.userName,
                    userBio: data.userBio,
                    userEmail: data.userEmail,
                    userPhone: data.userPhone,
                    clerkId: user.id,
                    profileImage: data.profileImage,
                    recommendations: data.recommendations
                });
                processedRecommendations = data.recommendations || [];
                setRecommendations(processedRecommendations);
                
                // Update pagination state for own profile
                if (data.pagination) {
                    const totalCount = data.pagination.total;
                    const limit = data.pagination.limit || 20;
                    setTotalPages(Math.ceil(totalCount / limit));
                    setTotalRecommendationCount(totalCount); // Update total count
                    // Set unfiltered count only on first load (when no filters are applied)
                    if (selectedServices.length === 0 && selectedCities.length === 0) {
                        setUnfilteredRecommendationCount(totalCount);
                    }
                }
            } else {
                setProfileInfo(data);
                const enrichedRecs = (data.recommendations || []).map(rec => ({
                    ...rec,
                    average_rating: parseFloat(rec.average_rating) || 0,
                    total_reviews: parseInt(rec.total_reviews, 10) || 0,
                    users_who_reviewed: rec.users_who_reviewed || []
                }));
                processedRecommendations = enrichedRecs;
                setRecommendations(enrichedRecs);
                
                // Reset image failed state when new profile data is loaded
                setImageFailed(false);
                
                // Update pagination state for public profile
                if (data.pagination) {
                    const totalCount = data.pagination.totalCount;
                    const limit = data.pagination.limit || 25;
                    setTotalPages(data.pagination.totalPages || Math.ceil(totalCount / limit));
                    setTotalRecommendationCount(totalCount); // Update total count
                    // Set unfiltered count only on first load (when no filters are applied)
                    if (selectedServices.length === 0 && selectedCities.length === 0) {
                        setUnfilteredRecommendationCount(totalCount);
                    }
                }
            }
            
            // Update current page
            setCurrentPage(page);
            
            // Fetch user score for Trust Points Wheel
            try {
                const scoreResponse = await fetch(`${API_URL}/api/users/preferred-name?email=${encodeURIComponent(data.userEmail || '')}`);
                if (scoreResponse.ok) {
                    const scoreData = await scoreResponse.json();
                    const score = parseInt(scoreData.userScore || scoreData.user_score) || 0;
                    setUserScore(score);
                } else {
                    setUserScore(0);
                }
            } catch (scoreError) {
                console.error("Error fetching user score:", scoreError);
                setUserScore(0);
            }
            
            // Fetch followers
            const targetClerkId = isOwnProfile ? user.id : (data.clerkId || null);
            if (targetClerkId) {
                const followersRes = await fetch(`${API_URL}/api/connections/followers?user_id=${targetClerkId}`);
                if (followersRes.ok) {
                    const followersData = await followersRes.json();
                    setConnections(Array.isArray(followersData) ? followersData : []);
                } else {
                    setConnections([]);
                }
            } else {
                setConnections([]);
            }
            
            // Set up liked map
            const initialLikes = new Map();
            processedRecommendations.forEach(r => {
                // Handle different boolean representations from PostgreSQL
                const isLiked = r.currentUserLiked === true || 
                               r.currentUserLiked === 'true' || 
                               r.currentUserLiked === 1 || 
                               r.currentUserLiked === '1';
                if (isLiked) {
                    initialLikes.set(r.id || r.provider_id, true);
                }
            });
            setLikedMap(initialLikes);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setIsLoadingPage(false);
        }
    }, [username, isLoaded, user, isSignedIn, isProfileOwnershipChecked, isOwnProfile, selectedServices, selectedCities]);

    // Add this function to handle page changes
    const handlePageChange = useCallback((page) => {
        if (page !== currentPage && page >= 1 && page <= totalPages) {
            fetchPageData(page);
        }
    }, [fetchPageData, currentPage, totalPages]);

    // Pagination component
    const Pagination = ({ currentPage, totalPages, onPageChange, isLoading }) => {
        const getPageNumbers = () => {
            const pages = [];
            const maxVisible = 7; // Show max 7 page numbers
            
            if (totalPages <= maxVisible) {
                // Show all pages if total is small
                for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Show first page, last page, and pages around current
                pages.push(1);
                
                if (currentPage > 3) {
                    pages.push('...');
                }
                
                const start = Math.max(2, currentPage - 1);
                const end = Math.min(totalPages - 1, currentPage + 1);
                
                for (let i = start; i <= end; i++) {
                    pages.push(i);
                }
                
                if (currentPage < totalPages - 2) {
                    pages.push('...');
                }
                
                if (totalPages > 1) {
                    pages.push(totalPages);
                }
            }
            
            return pages;
        };

        return (
            <div className="pagination-container">
                <div className="pagination-buttons">
                    {/* Previous button */}
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1 || isLoading}
                        className="pagination-button pagination-nav"
                    >
                        ← Previous
                    </button>
                    
                    {/* Page numbers */}
                    {getPageNumbers().map((page, index) => (
                        <button
                            key={index}
                            onClick={() => typeof page === 'number' ? onPageChange(page) : null}
                            disabled={page === '...' || isLoading}
                            className={`pagination-button pagination-number ${
                                page === currentPage ? 'active' : ''
                            } ${page === '...' ? 'ellipsis' : ''}`}
                        >
                            {page}
                        </button>
                    ))}
                    
                    {/* Next button */}
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages || isLoading}
                        className="pagination-button pagination-nav"
                    >
                        Next →
                    </button>
                </div>
                
                {/* Page info */}
                <div className="pagination-info">
                    Page {currentPage} of {totalPages}
                </div>
            </div>
        );
    };

    // Add this function to fetch connection status
    const fetchConnectionStatus = useCallback(async () => {
    if (!currentUserId || !profileInfo?.userId || currentUserId === profileInfo.userId || isOwnProfile) {
        return;
    }

    try {
        const response = await fetch(`${API_URL}/api/connections/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            fromUserId: currentUserId,
            toUserId: profileInfo.userId
        })
        });

        if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data.status);
        }
    } catch (error) {
        console.error('Error fetching connection status:', error);
    }
    }, [currentUserId, profileInfo?.userId, isOwnProfile]);

    const handleConfirmUnfollow = async () => {
        if (!currentUserId || !profileInfo?.userId || isFollowLoading) return;
    
        setIsFollowLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/connections/remove`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fromUserId: currentUserId,
                    toUserId: profileInfo.userId
                })
            });
    
            if (response.ok) {
                setConnectionStatus('not_connected');
                fetchPageData(); // Refresh data to update follower count, etc.
            } else {
                alert('Failed to unfollow user. Please try again.');
            }
        } catch (error) {
            console.error('Error removing connection:', error);
            alert('An error occurred while trying to unfollow.');
        } finally {
            setIsFollowLoading(false);
            setIsUnfollowModalOpen(false); // Close the modal regardless of outcome
        }
    };

    // Add this function to handle follow/unfollow
    const handleFollowToggle = async () => {
    if (!currentUserId || !profileInfo?.userId || isFollowLoading) return;

    if (connectionStatus === 'connected') {
        // Open the confirmation modal instead of directly unfollowing
        setIsUnfollowModalOpen(true);
        return; 
    }

    setIsFollowLoading(true);
    try {
        // Follow logic remains the same
        const response = await fetch(`${API_URL}/api/connections/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
            fromUserId: currentUserId,
            toUserId: profileInfo.userId
            })
        });

        if (response.ok) {
            const data = await response.json();
            setConnectionStatus(data.status === 'accepted' ? 'connected' : 'pending_outbound');
            // Refresh connections count
            fetchPageData();
        }
    } catch (error) {
        console.error('Error toggling follow status:', error);
        alert('Failed to update follow status');
    } finally {
        setIsFollowLoading(false);
    }
    };

    // Add this function to render the follow button
    const renderFollowButton = () => {
    if (!isSignedIn || !currentUserId || isOwnProfile) {
        return null;
    }

    const getButtonText = () => {
        switch (connectionStatus) {
            case 'connected':
                return <span className="follow-text">Following</span>;
            case 'pending_outbound':
                return <span className="follow-text">Requested</span>;
            case 'pending_inbound':
                return <span className="follow-text">Accept Request</span>;
            default:
                return <span className="follow-text">Follow</span>;
        }
    };

    const getButtonClass = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'follow-button following';
            case 'pending_outbound':
                return 'follow-button pending';
            case 'pending_inbound':
                return 'follow-button accept';
            default:
                return 'follow-button';
        }
    };

    return (
        <button
            className={getButtonClass()}
            onClick={handleFollowToggle}
            disabled={isFollowLoading}
        >
            {isFollowLoading ? (
                <>
                    <span>⏳</span>
                    <span>Loading...</span>
                </>
            ) : (
                getButtonText()
            )}
        </button>
            );
    };

    // Add New Recommendation button for own profile
    const renderAddNewButton = () => {
        if (!isOwnProfile) return null;
        
        return (
            <div className="profile-recommendations-controls">
                <button
                    className="profile-add-new-btn"
                    onClick={() => window.location.href = '/share-recommendation'}
                >
                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add New
                </button>
            </div>
        );
    };

    useEffect(() => {
        // Reset pagination when component mounts or dependencies change
        setCurrentPage(1);
        setTotalPages(1);
        fetchPageData(1);
    }, [fetchPageData]);

    // Reset pagination when search or filters change
    useEffect(() => {
        // When filters change, reset to page 1 and fetch new data
        setCurrentPage(1);
        fetchPageData(1);
    }, [selectedCities, selectedServices]);
    
    useEffect(() => {
        if (profileInfo && currentUserId && !isOwnProfile) {
            fetchConnectionStatus();
        }
    }, [profileInfo, currentUserId, fetchConnectionStatus, isOwnProfile]);

    // Fetch comments for recommendations
    useEffect(() => {
        if (!loading && recommendations.length > 0) {
            fetchBatchComments(recommendations);
        }
    }, [recommendations, loading]);

    const getInitials = (name, email) => {
        if (name) {
            const names = name.split(' ').filter(n => n);
            if (names.length > 1) { return (names[0][0] + names[names.length - 1][0]).toUpperCase(); }
            else if (names.length === 1 && names[0].length > 1) { return names[0].substring(0, 2).toUpperCase(); }
            else if (names.length === 1 && names[0].length) { return names[0][0].toUpperCase(); }
        }
        if (email && email.length > 0) return email[0].toUpperCase();
        return "U";
    };

    const handleImageError = () => {
        console.log('Profile image failed to load');
        setImageFailed(true);
    };

    const onAvatarOrPreviewError = (e) => {
        e.target.style.display = "none";
        const fallbackParent =
            e.target.closest(".profile-avatar-display-wrapper") ||
            e.target.closest(".profile-avatar-cropper-wrapper");
        if (fallbackParent) {
            const fallbackIcon = fallbackParent.querySelector(
                ".profile-avatar-icon-fallback, .profile-avatar-icon-editing"
            );
            if (fallbackIcon) fallbackIcon.style.display = "flex";
        }
    };

    const handleOpenReviewModal = (provider) => {
        // Create URL parameters for prefilling the recommendation form
        const params = new URLSearchParams();
        
        // Add the provider ID to link to existing service provider
        if (provider.id) {
            params.set('providerId', provider.id);
        }
        
        if (provider.business_name || provider.name) {
            params.set('businessName', provider.business_name || provider.name);
        }
        if (provider.contact_name) {
            params.set('contactName', provider.contact_name);
        }
        if (provider.website) {
            params.set('website', provider.website);
        }
        if (provider.phone_number) {
            params.set('phoneNumber', provider.phone_number);
        }
        if (provider.street_address) {
            params.set('streetAddress', provider.street_address);
        }
        if (provider.city) {
            params.set('city', provider.city);
        }
        if (provider.state) {
            params.set('state', provider.state);
        }
        if (provider.zip_code) {
            params.set('zipCode', provider.zip_code);
        }
        if (provider.google_place_id) {
            params.set('googlePlaceId', provider.google_place_id);
        }
        if (provider.latitude && provider.longitude) {
            params.set('latitude', provider.latitude);
            params.set('longitude', provider.longitude);
        }
        if (provider.tags && Array.isArray(provider.tags) && provider.tags.length > 0) {
            params.set('tags', provider.tags.join(', '));
        }
        
        // Navigate to share-recommendation page with prefilled data
        navigate(`/share-recommendation?${params.toString()}`);
    };

    const handleReviewSubmit = async (reviewData) => {
        if (!isSignedIn || !selectedProviderForReview || !currentUserId || !currentUserEmail) {
            alert("Please sign in to submit a review.");
            return;
        }
        const providerIdToUse = selectedProviderForReview.provider_id || selectedProviderForReview.id;
        try {
            const response = await fetch(`${API_URL}/api/reviews`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    provider_id: providerIdToUse,
                    provider_email: selectedProviderForReview.email || "",
                    user_id: currentUserId,
                    email: currentUserEmail,
                    rating: reviewData.rating, content: reviewData.review, tags: reviewData.tags,
                }),
            });
            if (!response.ok) throw new Error("Failed to submit review.");
            setReviewModalOpen(false);
            fetchPageData();
        } catch (err) {
            alert(`Error submitting review: ${err.message}`);
        }
    };

    const handleLikeToggle = async (providerId) => {
        if (!isSignedIn || !currentUserId || !currentUserEmail) {
            alert("Please log in to like recommendations.");
            return;
        }
        const originalRecs = JSON.parse(JSON.stringify(recommendations));
        const originalLikedMap = new Map(likedMap);
        const isCurrentlyLiked = likedMap.get(providerId) || false;
        setRecommendations(prev => prev.map(rec => {
            const currentRecId = rec.id || rec.provider_id;
            if (currentRecId === providerId) {
                return {
                    ...rec,
                    currentUserLiked: !isCurrentlyLiked,
                    num_likes: !isCurrentlyLiked ? (rec.num_likes || 0) + 1 : Math.max(0, (rec.num_likes || 0) - 1)
                };
            }
            return rec;
        }));
        setLikedMap(prev => new Map(prev).set(providerId, !isCurrentlyLiked));
        try {
            const response = await fetch(`${API_URL}/api/providers/${providerId}/like`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userId: currentUserId, userEmail: currentUserEmail })
            });
            const result = await response.json();
            if (!result.success) {
                throw new Error(result.message || 'Failed to update like status.');
            }
            setRecommendations(prev => prev.map(rec => {
                const currentRecId = rec.id || rec.provider_id;
                if (currentRecId === providerId) {
                    return { ...rec, num_likes: result.num_likes, currentUserLiked: result.currentUserLiked };
                }
                return rec;
            }));
            setLikedMap(prev => new Map(prev).set(providerId, result.currentUserLiked));
        } catch (error) {
            alert(`Failed to update like: ${error.message}`);
            setRecommendations(originalRecs);
            setLikedMap(originalLikedMap);
        }
    };

    const sortedRecommendations = useMemo(() => {
        // Filtering is now handled by the backend API
        // The recommendations returned are already filtered based on searchQuery, selectedServices, and selectedCities
        return recommendations;
    }, [recommendations]);

    // Batch fetch comments for multiple recommendations
    const fetchBatchComments = async (recommendations) => {
        if (!recommendations || recommendations.length === 0) return;
        
        setIsLoadingComments(true);
        try {
            const serviceIds = recommendations.map(rec => rec.provider_id || rec.id).filter(Boolean);
            
            if (serviceIds.length === 0) return;

            const response = await fetch(`${API_URL}/api/comments/batch`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ service_ids: serviceIds })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.comments) {
                    const commentsMap = new Map();
                    Object.entries(data.comments).forEach(([serviceId, comments]) => {
                        commentsMap.set(serviceId, comments || []);
                    });
                    setCommentsMap(commentsMap);
                }
            }
        } catch (error) {
            console.error('Error fetching batch comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    // Handle new comment added
    const handleCommentAdded = (serviceId, newComment) => {
        setCommentsMap(prev => {
            const newMap = new Map(prev);
            const existingComments = newMap.get(serviceId) || [];
            newMap.set(serviceId, [newComment, ...existingComments]);
            return newMap;
        });
    };

    const handleProviderClick = (providerId) => {
        navigate(`/provider/${providerId}`);
    };

    // Initialize editing bio when profile info loads
    useEffect(() => {
        if (profileInfo?.userBio && !editingBio) {
            setEditingBio(profileInfo.userBio);
        }
    }, [profileInfo?.userBio, editingBio]);

    const getClerkUserQueryParams = useCallback(() => {
        if (!user) return "";
        return new URLSearchParams({
            user_id: user.id,
            email: user.primaryEmailAddress?.emailAddress,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            phoneNumber: user.primaryPhoneNumber?.phoneNumber || "",
        }).toString();
    }, [user]);

    // Profile editing functions
    const handleEditToggle = () => {
        if (isEditing) {
            setEditingBio(profileInfo?.userBio || "");
            setImgSrcForCropper("");
            setCompletedCrop(null);
            setOriginalFile(null);
            setCrop(undefined);
        } else {
            setEditingBio(profileInfo?.userBio || "");
        }
        setIsEditing(!isEditing);
    };

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const cropWidthInPercent = (MIN_DIMENSION / width) * 100;
        const cropVal = makeAspectCrop(
            { unit: "%", width: cropWidthInPercent },
            ASPECT_RATIO,
            width,
            height
        );
        const centeredCrop = centerCrop(cropVal, width, height);
        setCrop(centeredCrop);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setOriginalFile(file);
            setCrop(undefined);
            const reader = new FileReader();
            reader.addEventListener("load", () =>
                setImgSrcForCropper(reader.result?.toString() || "")
            );
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = async () => {
        if (!user) {
            setError("User not found.");
            return;
        }
        setError(null);
        setSaving(true);
        const formData = new FormData();
        formData.append("bio", editingBio);
        formData.append("firstName", user.firstName || "");
        formData.append("lastName", user.lastName || "");
        
        let fileToUpload = null;
        if (completedCrop && imgRef.current && originalFile) {
            try {
                const croppedImageBlob = await getCroppedImg(
                    imgRef.current,
                    completedCrop,
                    originalFile.name
                );
                fileToUpload = new File([croppedImageBlob], originalFile.name, {
                    type: croppedImageBlob.type,
                });
            } catch (cropError) {
                setError("Failed to crop image.");
                setSaving(false);
                return;
            }
        }
        if (fileToUpload) formData.append("profileImageFile", fileToUpload);
        
        try {
            const queryParams = getClerkUserQueryParams();
            if (!queryParams)
                throw new Error("User details not available for API query.");
            const response = await fetch(
                `${API_URL}/api/users/me/profile?${queryParams}`,
                { method: "PUT", body: formData }
            );
            const data = await response.json();
            if (!response.ok || !data.success)
                throw new Error(data.message || "Failed to update profile.");
            
            // Update local state
            setProfileInfo(prev => ({
                ...prev,
                userBio: data.user.bio || "",
                userName: data.user.name
            }));
            setEditingBio(data.user.bio || "");
            
            // Note: Profile image upload functionality needs to be updated to handle GCS storage
            // For now, refresh the page data to get the updated profile image URL
            fetchPageData();
            
            setImgSrcForCropper("");
            setCompletedCrop(null);
            setOriginalFile(null);
            setIsEditing(false);
        } catch (err) {
            setError(`Failed to save profile: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (isOwnProfile) {
        return <Profile />;
    }

    if (loading || !isProfileOwnershipChecked) {
        return <div className="profile-loading-container"><div className="profile-spinner"></div><p>Loading Profile...</p></div>;
    }
    if (error) {
        return <div className="profile-error-banner">{error}</div>;
    }
    if (!profileInfo) {
        return <div className="profile-empty-state"><p>User not found or profile could not be loaded.</p></div>;
    }

    const { userName, userBio, userEmail: profileUserEmail, userPhone: profileUserPhone, profileImage } = profileInfo;

    return (
        <div className="profile-page-container">
            <header className="profile-hero-header">
                <div className="profile-hero-content">
                    <div className="profile-avatar-wrapper">
                        {isOwnProfile && isEditing ? (
                            <div className="profile-avatar-cropper-wrapper">
                                <input
                                    type="file"
                                    id="profileImageUploadInput"
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {!imgSrcForCropper && (
                                    <div
                                        className="profile-avatar-container"
                                        onClick={() =>
                                            document
                                                .getElementById(
                                                    "profileImageUploadInput"
                                                )
                                                ?.click()
                                        }
                                        style={{ cursor: "pointer" }}
                                    >
                                                                            {profileImage && !imageFailed ? (
                                        <img
                                            src={profileImage}
                                            alt="Current profile"
                                            className="profile-avatar-image"
                                            onError={onAvatarOrPreviewError}
                                        />
                                    ) : (
                                        <div className="profile-avatar-initials">
                                            <span>{getInitials(userName, profileUserEmail)}</span>
                                        </div>
                                    )}
                                        <div className="profile-avatar-edit-overlay">
                                            <CameraIcon
                                                style={{
                                                    width: "24px",
                                                    height: "24px",
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {imgSrcForCropper && (
                                    <div className="cropper-container">
                                        <ReactCrop
                                            crop={crop}
                                            onChange={(pc, p) => setCrop(p)}
                                            onComplete={(c) => setCompletedCrop(c)}
                                            aspect={ASPECT_RATIO}
                                            minWidth={MIN_DIMENSION}
                                            minHeight={MIN_DIMENSION}
                                            circularCrop={true}
                                        >
                                            <img
                                                ref={imgRef}
                                                src={imgSrcForCropper}
                                                alt="Crop me"
                                                onLoad={onImageLoad}
                                                style={{ maxHeight: "300px" }}
                                            />
                                        </ReactCrop>
                                        <button
                                            className="profile-change-photo-btn-cropper"
                                            onClick={() =>
                                                document
                                                    .getElementById(
                                                        "profileImageUploadInput"
                                                    )
                                                    ?.click()
                                            }
                                        >
                                            <CameraIcon className="btn-icon" />{" "}
                                            Change Photo
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                                            {profileImage && !imageFailed ? (
                                <img
                                    src={profileImage}
                                    alt={userName || profileUserEmail}
                                    className="profile-avatar-image"
                                    onError={handleImageError}
                                    onLoad={() => console.log('Profile image loaded successfully')}
                                />
                            ) : (
                                <div className="profile-avatar-initials">
                                    <span>{getInitials(userName, profileUserEmail)}</span>
                                </div>
                            )}
                            </>
                        )}
                    </div>
                    <div className="profile-details-wrapper">
                        <div className="profile-header-top">
                            <h1 className="profile-user-name">{userName || "User Profile"}</h1>
                            <div className="profile-header-actions">
                                {isOwnProfile ? (
                                    isEditing ? (
                                        <>
                                            <button
                                                className="profile-save-btn"
                                                onClick={handleSaveProfile}
                                                disabled={saving}
                                            >
                                                <CheckCircleIcon className="btn-icon" />{" "}
                                                {saving ? "Saving..." : "Save Changes"}
                                            </button>
                                            <button
                                                className="profile-cancel-btn"
                                                onClick={handleEditToggle}
                                                disabled={saving}
                                            >
                                                <XCircleIcon className="btn-icon" /> Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className="profile-edit-btn"
                                                onClick={handleEditToggle}
                                            >
                                                <PencilSquareIcon className="btn-icon" /> Edit
                                                Profile
                                            </button>
                                            <button
                                                className="profile-share-btn-header"
                                                onClick={() => setIsShareModalOpen(true)}
                                                disabled={saving || isEditing}
                                            >
                                                <ShareIcon className="btn-icon" />{" "}
                                                Share Profile
                                            </button>
                                        </>
                                    )
                                ) : (
                                    renderFollowButton()
                                )}
                            </div>
                        </div>
                        
                        <div className="profile-contact-info">
                            {profileUserPhone && (
                                <a 
                                    href={`sms:${profileUserPhone.replace(/\D/g, '')}`} 
                                    className="profile-contact-link phone"
                                    title="Send a text to this person"
                                >
                                    <svg className="contact-icon" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                                    </svg>
                                    {profileUserPhone}
                                </a>
                            )}
                            
                            {profileUserEmail && (
                                <a 
                                    href={`mailto:${profileUserEmail}`} 
                                    className="profile-contact-link email"
                                    title="Send email to this person"
                                >
                                    <EnvelopeIcon className="contact-icon" />
                                    {profileUserEmail}
                                </a>
                            )}
                        </div>
                        
                        {isOwnProfile && isEditing ? (
                            <textarea
                                className="profile-bio-textarea"
                                value={editingBio}
                                onChange={(e) => setEditingBio(e.target.value)}
                                placeholder="Tell us about yourself..."
                                rows={3}
                            />
                        ) : (
                            userBio && (
                                <blockquote className="profile-user-bio">
                                    <p>{userBio}</p>
                                </blockquote>
                            )
                        )}
                    </div>
                    <div className="profile-stats-wrapper">
                        <div className="profile-top-stats">
                            <div className="profile-stat-item">
                                <span className="profile-stat-number">{unfilteredRecommendationCount}</span>
                                <span className="profile-stat-label">Recommendations</span>
                            </div>
                            <div className="profile-stat-item">
                                <span className="profile-stat-number">{connections.length}</span>
                                <span className="profile-stat-label">Followers</span>
                            </div>
                        </div>
                        <div className="profile-stat-item">
                            <div className="profile-trust-score-wrapper">
                                <TrustScoreWheel 
                                    score={userScore} 
                                    showDebug={false}
                                />
                            </div>
                        </div>
                        <div className="profile-stat-item profile-achievement-hidden" style={{display: 'none'}}>
                            <div className="profile-stat-achievement">
                                <AchievementBadge recCount={unfilteredRecommendationCount} />
                            </div>
                            <span className="profile-stat-label">
                                {(() => {
                                    const count = unfilteredRecommendationCount;
                                    if (count >= 100) return 'Diamond Recommender';
                                    if (count >= 50) return 'Platinum Recommender';
                                    if (count >= 25) return 'Gold Recommender';
                                    if (count >= 10) return 'Silver Recommender';
                                    if (count >= 1) return 'Bronze Recommender';
                                    return 'Getting Started';
                                })()}
                            </span>
                            {(() => {
                                const count = unfilteredRecommendationCount;
                                let nextTier, remaining;
                                if (count < 1) {
                                    nextTier = 'Bronze';
                                    remaining = 1 - count;
                                } else if (count < 10) {
                                    nextTier = 'Silver';
                                    remaining = 10 - count;
                                } else if (count < 25) {
                                    nextTier = 'Gold';
                                    remaining = 25 - count;
                                } else if (count < 50) {
                                    nextTier = 'Platinum';
                                    remaining = 50 - count;
                                } else if (count < 100) {
                                    nextTier = 'Diamond';
                                    remaining = 100 - count;
                                } else {
                                    return null; // Max tier reached
                                }
                                return (
                                    <span className="profile-stat-progress">
                                        {count === 0 ? 'No recommendations yet' : `${remaining} more to reach ${nextTier}`}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </header>

            <main className="profile-content-area">
                <div className="profile-recommendations-header">
                    <div className="profile-recommendations-title-section">
                        <h2>{isOwnProfile ? "My Recent Activity" : `${userName ? `${userName}'s` : ""} Recent Activity`}</h2>
                        {renderAddNewButton()}
                    </div>

                    <div className="filters-container">
                        {availableServices.length > 0 && (
                            <div className="profile-city-filter-toggle-section">
                                <button
                                    className="profile-city-filter-toggle"
                                    onClick={() =>
                                        setShowServiceFilter(
                                            !showServiceFilter
                                        )
                                    }
                                >
                                    <FaConciergeBell className="profile-filter-icon" />
                                    <span className="filter-button-text">
                                        <span className="filter-button-text-long">
                                            Filter by{" "}
                                        </span>
                                        <span>Service</span>
                                    </span>
                                    {selectedServices.length > 0 && (
                                        <span className="profile-active-filters-badge">
                                            {selectedServices.length}
                                        </span>
                                    )}
                                    <ChevronDownIcon
                                        className={`profile-filter-chevron ${
                                            showServiceFilter
                                                ? "rotated"
                                                : ""
                                        }`}
                                    />
                                </button>
                            </div>
                        )}
                        {availableCities.length > 1 && (
                            <div className="profile-city-filter-toggle-section">
                                <button
                                    className="profile-city-filter-toggle"
                                    onClick={() =>
                                        setShowCityFilter(
                                            !showCityFilter
                                        )
                                    }
                                >
                                    <FaMapMarkerAlt className="profile-filter-icon" />
                                    <span className="filter-button-text">
                                        <span className="filter-button-text-long">
                                            Filter by{" "}
                                        </span>
                                        <span>City</span>
                                    </span>
                                    {selectedCities.length > 0 && (
                                        <span className="profile-active-filters-badge">
                                            {selectedCities.length}
                                        </span>
                                    )}
                                    <ChevronDownIcon
                                        className={`profile-filter-chevron ${
                                            showCityFilter
                                                ? "rotated"
                                                : ""
                                        }`}
                                    />
                                </button>
                            </div>
                        )}
                        {showServiceFilter && availableServices.length > 0 && (
                            <div className="profile-city-filter-wrapper">
                                <div className="profile-city-filter-checkboxes">
                                    {availableServices.map(
                                        (service) => (
                                            <div
                                                key={service.id}
                                                className="profile-city-checkbox-item"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`service-${service.id}`}
                                                    name={service.name}
                                                    checked={selectedServices.includes(
                                                        service.id
                                                    )}
                                                    onChange={() =>
                                                        handleServiceSelection(
                                                            service.id
                                                        )
                                                    }
                                                />
                                                <label
                                                    htmlFor={`service-${service.id}`}
                                                    className="profile-city-checkbox-label"
                                                >
                                                    {service.name}
                                                </label>
                                                <span className="profile-city-count">
                                                    ({service.count})
                                                </span>
                                            </div>
                                        )
                                    )}
                                    {selectedServices.length > 0 && (
                                        <button
                                            onClick={() =>
                                                setSelectedServices([])
                                            }
                                            className="profile-city-clear-all"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        {showCityFilter && availableCities.length > 1 && (
                            <div className="profile-city-filter-wrapper">
                                <div className="profile-city-filter-checkboxes">
                                    {availableCities.map(
                                        (city) => (
                                            <div
                                                key={city.name}
                                                className="profile-city-checkbox-item"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`city-${city.name.replace(
                                                        /\s+/g,
                                                        '-'
                                                    )}`}
                                                    name={city.name}
                                                    checked={selectedCities.includes(
                                                        city.name
                                                    )}
                                                    onChange={() =>
                                                        handleCitySelection(
                                                            city.name
                                                        )
                                                    }
                                                />
                                                <label
                                                    htmlFor={`city-${city.name.replace(
                                                        /\s+/g,
                                                        '-'
                                                    )}`}
                                                    className="profile-city-checkbox-label"
                                                >
                                                    {city.name}
                                                </label>
                                                <span className="profile-city-count">
                                                    ({city.count})
                                                </span>
                                            </div>
                                        )
                                    )}
                                    {selectedCities.length > 0 && (
                                        <button
                                            onClick={() =>
                                                setSelectedCities([])
                                            }
                                            className="profile-city-clear-all"
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    {sortedRecommendations.length > 0 && (
                        <span className="profile-recommendations-count">
                            {sortedRecommendations.length} of {unfilteredRecommendationCount} recommendations
                            {totalPages > 1 ? ` (page ${currentPage} of ${totalPages})` : ''}
                        </span>
                    )}
                </div>
                
                {sortedRecommendations.length > 0 ? (
                    <>
                        {/* View Mode Tabs */}
                        <div className="view-mode-tabs">
                            <button
                                className={`view-mode-tab ${
                                    viewMode === "list" ? "active" : ""
                                }`}
                                onClick={() => setViewMode("list")}
                            >
                                <FaList className="tab-icon" />
                                <span>List View</span>
                                <span className="result-count">
                                    ({sortedRecommendations.length})
                                </span>
                            </button>
                            <button
                                className={`view-mode-tab ${
                                    viewMode === "map" ? "active" : ""
                                }`}
                                onClick={() => setViewMode("map")}
                            >
                                <FaMap className="tab-icon" />
                                <span>Map View</span>
                                <span className="result-count">
                                    (
                                    {
                                        sortedRecommendations.filter(
                                            (rec) => rec.latitude && rec.longitude
                                        ).length
                                    }
                                    )
                                </span>
                            </button>
                        </div>

                        {/* List View */}
                        {viewMode === "list" && (
                            <div className="recommendations-feed">
                                {sortedRecommendations.map((rec, index) => (
                                    <RecommendationCard
                                        key={`${rec.id || rec.provider_id}-${currentPage}-${index}`}
                                        rec={rec}
                                        onWriteReview={handleOpenReviewModal}
                                        onLike={handleLikeToggle}
                                        isLikedByCurrentUser={likedMap.get(rec.id || rec.provider_id) || false}
                                        loggedInUserId={currentUserId}
                                        currentUserName={userName}
                                        comments={commentsMap.get(String(rec.provider_id || rec.id)) || []}
                                        onCommentAdded={handleCommentAdded}
                                        showEditDelete={isOwnProfile}
                                        onEdit={isOwnProfile ? (rec) => {
                                            setSelectedRecForEdit(rec);
                                            setIsEditModalOpen(true);
                                        } : undefined}
                                        onRefreshList={fetchPageData}
                                        showPersonalRating={true}
                                        hideReviewCount={true}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Map View */}
                        {viewMode === "map" && (
                            <div className="map-view-container">
                                <MapView
                                    providers={sortedRecommendations.filter(
                                        (rec) => rec.latitude && rec.longitude
                                    )}
                                    userLocation={userLocation}
                                    onProviderClick={handleProviderClick}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    (selectedCities.length > 0 || selectedServices.length > 0) ? (
                        <div className="profile-empty-state no-search-results">
                            <svg className="no-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <p>
                                No recommendations found
                                {selectedServices.length > 0 && ` for ${selectedServices.join(", ")}`}
                                {selectedCities.length > 0 && ` in ${selectedCities.join(", ")}`}
                            </p>
                            <p className="search-suggestions">
                                Try selecting different cities or clearing filters.
                            </p>
                            <div className="profile-filter-clear-buttons">
                                {selectedServices.length > 0 && (
                                    <button
                                        className="profile-secondary-action-btn"
                                        onClick={() => setSelectedServices([])}
                                    >
                                        Clear Service Filters
                                    </button>
                                )}
                                {selectedCities.length > 0 && (
                                    <button
                                        className="profile-secondary-action-btn"
                                        onClick={() => setSelectedCities([])}
                                    >
                                        Clear City Filters
                                    </button>
                                )}
                                {(selectedCities.length > 0 || selectedServices.length > 0) && (
                                    <button
                                        className="profile-secondary-action-btn"
                                        onClick={() => {
                                            setSelectedCities([]);
                                            setSelectedServices([]);
                                        }}
                                    >
                                        Clear All Filters
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="profile-empty-state no-providers-message">
                            <FaStar className="no-providers-icon" />
                            <p>
                                {isOwnProfile ? "You haven't" : (userName ? `${userName} hasn't` : "This user hasn't")} made any recommendations yet.
                            </p>
                            {isOwnProfile && (
                                <button 
                                    className="profile-primary-action-btn"
                                    onClick={() => window.location.href = '/share-recommendation'}
                                >
                                    Share Your First Recommendation
                                </button>
                            )}
                        </div>
                    )
                )}
                
                {/* Pagination - show if there are recommendations and multiple pages */}
                {sortedRecommendations.length > 0 && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        isLoading={isLoadingPage}
                    />
                )}
            </main>

            <UnfollowConfirmationModal
                isOpen={isUnfollowModalOpen}
                onClose={() => setIsUnfollowModalOpen(false)}
                onConfirm={handleConfirmUnfollow}
                userName={profileInfo?.userName || 'this user'}
                isLoading={isFollowLoading}
            />

            {selectedProviderForReview && (
                <ReviewModal
                    isOpen={reviewModalOpen}
                    onClose={() => setReviewModalOpen(false)}
                    onSubmit={handleReviewSubmit}
                    provider={selectedProviderForReview}
                />
            )}

            {isShareModalOpen && (
                <ShareProfileModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    profileData={profileInfo}
                    userEmail={user?.primaryEmailAddress?.emailAddress}
                />
            )}

            {isEditModalOpen && selectedRecForEdit && user && (
                <div className="profile-edit-modal-overlay">
                    <div className="profile-edit-modal-content">
                        <button
                            onClick={() => {
                                setIsEditModalOpen(false);
                                setSelectedRecForEdit(null);
                            }}
                            className="profile-edit-modal-close-btn"
                        >
                            &times;
                        </button>
                        <h2 className="profile-edit-modal-title">
                            Edit Your Recommendation
                        </h2>
                        <p style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                            Edit recommendation functionality will be available soon. 
                            For now, you can delete and recreate your recommendation.
                        </p>
                        <div className="profile-edit-modal-button-row">
                            <button
                                className="profile-edit-modal-btn cancel-btn"
                                onClick={() => {
                                    setIsEditModalOpen(false);
                                    setSelectedRecForEdit(null);
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicProfile;
